import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import MoldStatus from '../shared/MoldStatus';
import MoldShotProgressGradient from '../shared/MoldShotProgressGradient';
import { Mold } from '../../types/mold';
import SecondaryButton from '../shared/SecondaryButton';
import CustomerCodeLabel from '../shared/CustomerCodeLabel';

interface MoldCardProps {
  mold: Mold;
  isSelected: boolean;
  onSelect: (moldId: string) => void;
}

const MoldCard: React.FC<MoldCardProps> = ({ mold, isSelected, onSelect }) => {
  const navigate = useNavigate();

  const handleViewDetails = (e: React.MouseEvent): void => {
    e.stopPropagation();
    navigate(`/molds/${mold.id}?source=mold_list`);
  };

  const handleSelectToggle = (e: React.MouseEvent): void => {
    e.stopPropagation();
    onSelect(mold.id);
  };

  return (
    <div
      className={`
        relative bg-white rounded-lg shadow-sm p-6 cursor-pointer
        transition-all duration-200 hover:shadow-md
        ${isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:border-gray-300'}
      `}
    >
      <Link
        to={`/molds/${mold.id}?source=mold_list`}
        aria-label={`เปิดแม่พิมพ์ ${mold.sbiId}`}
        className="absolute inset-0 z-10 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      />
      <div className="flex justify-between items-start mb-4">
        <div className="flex flex-row items-center justify-center gap-2">
          <h3 className="text-xl font-semibold text-gray-900">{mold.sbiId}</h3>
          <CustomerCodeLabel customer={mold.customer} />
        </div>
        <MoldStatus mold={mold} />
      </div>

      <div className="mb-4">
        <p className="text-sm text-gray-600 font-medium mb-2">รหัสชิ้นงาน</p>
        <div className="flex flex-wrap gap-1.5">
          {mold.productParts.map((part) => (
            <span
              key={part.partNumber}
              className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium hover:bg-gray-200 transition-colors"
            >
              {part.partNumber}
            </span>
          ))}
        </div>
      </div>

      <div className="mt-6 mb-6">
        <div className="flex justify-between items-center mb-2">
          <p className="text-sm text-gray-600 font-medium">ชอตสะสมในรอบล้าง:</p>
          <span className="text-sm font-medium text-gray-900">
            {mold.cycleShotCount.toLocaleString()} ชอต
          </span>
        </div>
        <MoldShotProgressGradient
          shotCount={mold.cycleShotCount}
          servicingAlertShotThreshold={mold.servicingAlertShotThreshold}
          servicingMaxShotThreshold={mold.servicingMaxShotThreshold}
          maxShotCount={mold.maxShotCount}
        />
      </div>

      <div className="flex justify-between items-center">
        <span className="text-sm p-2 rounded-full bg-blue-100 text-blue-800">
          {mold?.moldNumber}
        </span>
        <div className="relative z-20 flex gap-1">
          <SecondaryButton
            text={isSelected ? 'เลือกแล้ว': 'เลือก'}
            Icon={isSelected ? Check : null}
            onClick={handleSelectToggle}
            className={isSelected ? 'bg-blue-700 text-white hover:bg-blue-600' : 'hover:bg-gray-100 transition-colors'}
          />
          <SecondaryButton
            text="ดูรายละเอียด"
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => handleViewDetails(e)}
            className="hover:bg-gray-100 transition-colors"
          />
        </div>
      </div>
    </div>
  );
};

export default MoldCard;
